import { site } from '../content/site'
import type { LeadSubmission } from '../types'

const apiOrigin = import.meta.env.VITE_API_URL
  || (import.meta.env.DEV ? 'http://localhost:8787' : site.apiOrigin)

interface ApiPayload {
  success: boolean
  id?: string
  key?: string
  error?: string
}

async function readPayload(response: Response): Promise<ApiPayload> {
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('application/json')) {
    return { success: false, error: 'The server returned an unexpected response.' }
  }
  const value: unknown = await response.json()
  if (!value || typeof value !== 'object' || !('success' in value) || typeof value.success !== 'boolean') {
    return { success: false, error: 'The server returned an invalid response.' }
  }

  return {
    success: value.success,
    id: 'id' in value && typeof value.id === 'string' ? value.id : undefined,
    key: 'key' in value && typeof value.key === 'string' ? value.key : undefined,
    error: 'error' in value && typeof value.error === 'string' ? value.error : undefined,
  }
}

export async function uploadLeadPhoto(file: File, uploadSession: string): Promise<string> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('uploadSession', uploadSession)

  const response = await fetch(`${apiOrigin}/api/uploads`, {
    method: 'POST',
    body: formData,
  })
  const payload = await readPayload(response)

  if (!response.ok || !payload.success || !payload.key) {
    throw new Error(payload.error ?? 'Photo upload failed.')
  }

  return payload.key
}

export async function submitLead(lead: LeadSubmission): Promise<string> {
  const response = await fetch(`${apiOrigin}/api/leads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(lead),
  })
  const payload = await readPayload(response)

  if (!response.ok || !payload.success || !payload.id) {
    throw new Error(payload.error ?? 'Your request could not be sent.')
  }

  return payload.id
}
